// @ts-nocheck
import moment from 'moment';

import { YYYY_MM_DD_DATE_FORMAT } from '../../const/CommonConst';

const momentTzInstance = (...args) => {
  const timeZone = localStorage.getItem('timeZone');

  if (timeZone) {
    return moment(...args).utcOffset(timeZone);
  }
  return moment(...args);
};
// Preserve moment's static API (isMoment, ISO_8601, utc, localeData, min, max, ...)
// so momentTz can be used as a drop-in replacement for moment
export const momentTz = Object.assign(momentTzInstance, moment);

export function getMonthStartEndDates(date) {
  const momentDate = momentTz(date);
  if (!momentDate.isValid()) {
    return {};
  }

  const monthNumber = momentDate.month() + 1; // Add 1 to get the actual month number
  const year = momentDate.year();

  // Create a momentTz object for the first day and last day of the month
  const startDate = momentTz(`${year}-${monthNumber}-01`);
  const endDate = startDate.clone().endOf('month');

  return {
    startDate: startDate.format(YYYY_MM_DD_DATE_FORMAT),
    endDate: endDate.format(YYYY_MM_DD_DATE_FORMAT),
  };
}

/**
 * Calculates the date based on the given frequency.
 * @param {string} frequency - The frequency to calculate the date for.
 *   Possible values are 'Monthly', 'Quarterly', or 'Yearly'.
 * @param {Date} date - The reference date.
 * @returns {Date} The calculated date based on the given frequency.
 */
export const calculateFrequencyDate = (frequency, date) => {
  let calculatedDate = date;
  switch (frequency) {
    case 'Monthly':
      calculatedDate = momentTz(date).add(1, 'M');
      break;
    case 'Quarterly':
      calculatedDate = momentTz(date).add(3, 'M');
      break;
    case 'Yearly':
      calculatedDate = momentTz(date).add(1, 'Y');
      break;
    default:
      break;
  }

  return calculatedDate;
};

/**
 * Calculate utilization rate in percentage given total efforts and available efforts in a week (in days)
 * If total efforts is not given, it defaults to 0 (in days)
 * If available efforts in a week is not given, it defaults to 5 (in days)
 * @param {number} totalEfforts total efforts
 * @param {number} [availableEffortsInWeek=5] available efforts in a week
 * @returns {number} utilization rate in percentage
 */
export const calculateUtilizationRate = (totalEfforts) => {
  const availableEffortsInWeek = 5; // in days
  return Math.round((totalEfforts / availableEffortsInWeek) * 100);
};

/**
 * @function extractMonth
 *@description  Extracts the month from a date string in UTC format.
 * @param {string} dateString - The date string in UTC format.
 * @returns {string} The full month name extracted from the date.
 */
export const extractMonth = (dateString) => {
  const utcDate = momentTz.utc(dateString, 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ');
  return utcDate.format('MMMM'); // or "MMM" for abbreviated month
};

/**
 * @function formatLastVisitedLabel
 *@description  Formats the last visited label based on the time difference between the current date and the last visited date.
 * @param {string} value - The last visited date in UTC format.
 * @returns {string} The formatted last visited label.
 */
export const formatLastVisitedLabel = (value) => {
  const date = new Date(value);
  if (!value || isNaN(date)) return null;

  const seconds = Math.floor((Date.now() - date) / 1000);
  const units = [
    [31536000, 'y'],
    [2592000, 'mo'],
    [86400, 'd'],
    [3600, 'h'],
    [60, 'm'],
  ];

  for (const [size, unit] of units) {
    if (Math.abs(seconds) >= size) {
      const time = Math.floor(Math.abs(seconds) / size);
      return `Last visited ${seconds < 0 ? `in ${time}${unit}` : `${time}${unit} ago`}`;
    }
  }

  return 'Last visited just now';
};
